<template>
  <div class="tech-support-page">
    <!-- 导航组件 -->
    <ProductNavigation />

    <!-- 页面头部 -->
    <section class="tech-support-hero-section">
    
    </section>

    <!-- 内容区域 -->

    <!-- 新增资源下载界面 -->
    <section class="resources-download-section">
  <div class="section-content">
    <h2>{{ t('techSupport.resourcesDownload') }}</h2>
    
    <!-- 选项卡导航 -->
    <div class="download-tabs">
      <button :class="['tab-btn', { 'active': currentTab === 'manual' }]" @click="currentTab = 'manual'">{{ t('techSupport.manualDownload') }}</button>
      <button :class="['tab-btn', { 'active': currentTab === 'software' }]" @click="currentTab = 'software'">{{ t('techSupport.softwareDownload') }}</button>
    </div>
    
    <!-- 选项卡内容 -->
    <div class="tab-content">
      <!-- 软件下载部分 -->
      <div v-if="currentTab === 'software'" class="software-download tab-panel">
        <div class="download-grid">
          <a
            v-for="item in downloadResources.software"
            :key="item.id"
            class="download-card"
            @click.prevent="handleDownload(item)"
          >
            <div class="download-icon"><i :class="`fas ${item.icon || 'fa-download'}`"></i></div>
            <div class="download-info">
              <h4 class="download-name">{{ item.name }}</h4>
              <p class="download-desc">{{ item.description }}</p>
              <span class="download-size">{{ item.size }}</span>
            </div>
            <span class="download-btn">{{ t('techSupport.download') }}</span>
          </a>
          <div v-if="!downloadResources.software || downloadResources.software.length === 0" class="no-data">{{ t('techSupport.noSoftware') }}</div>
        </div>
      </div>
      
      <!-- 说明书下载部分 -->
      <div v-else class="manual-download tab-panel">
        <div class="download-grid">
          <a
            v-for="item in downloadResources.manual"
            :key="item.id"
            href="#"
            class="download-card"
            @click.prevent="handleDownload(item)"
          >
            <div class="download-icon"><i :class="`fas ${item.icon || 'fa-file-pdf'}`"></i></div>
            <div class="download-info">
              <h4 class="download-name">{{ item.name }}</h4>
              <p class="download-desc">{{ item.description }}</p>
              <span class="download-size">{{ item.size }}</span>
            </div>
            <span class="download-btn">{{ t('techSupport.download') }}</span>
          </a>
          <div v-if="!downloadResources.manual || downloadResources.manual.length === 0" class="no-data">{{ t('techSupport.noManual') }}</div>
        </div>
      </div>
    </div>
  </div>
    </section>

    <section class="tech-support-content-section">
      <div class="section-content">
        <!-- 常见问题 -->
        <div class="faq-section">
          <h3>{{ t('techSupport.faq') }}</h3>
          <div class="faq-list">
            <div v-for="faq in faqs" :key="faq.id" class="faq-item">
              <div class="faq-question">{{ t('techSupport.question') }}: {{ faq.question }}</div>
              <div class="faq-answer">{{ t('techSupport.answer') }}: {{ faq.answer }}</div>
            </div>
            <div v-if="!faqs || faqs.length === 0" class="no-data">{{ t('techSupport.noFaq') }}</div>
          </div>
        </div>

        <!-- 技术文档下载 -->
        <div class="documents-section">
          <h3>{{ t('techSupport.technicalDocs') }}</h3>
          <div class="documents-list">
            <a
              v-for="item in documents"
              :key="item.id"
              class="document-item"
              @click.prevent="handleDownload(item)"
            >
              <span class="document-name">{{ item.title }}</span>
              <span class="document-format">{{ getFileType(item.url || item.fileName || '') }}</span>
            </a>
            <div v-if="!documents || documents.length === 0" class="no-data">{{ t('techSupport.noDocuments') }}</div>
          </div>
        </div>

      </div>
    </section>

    <!-- 页脚 -->
    <Footer />
  </div>
</template>

<script setup>
import ProductNavigation from '@/components/ProductNavigation.vue';
import Footer from '@/components/Footer.vue';
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
// 导入API函数
import { getResources, getFaqs, getDocuments } from '@/api/techSupport';
import { downloadFile } from '@/api/techSupport';

const { t, locale } = useI18n();

// 定义当前选中的选项卡
const currentTab = ref('software');

// 获取文件类型显示
const getFileType = (fileUrl) => {
  if (!fileUrl) return 'FILE';
  const extension = fileUrl.split('.').pop()?.toLowerCase();
  const typeMap = {
    'pdf': 'PDF',
    'doc': 'DOC',
    'docx': 'DOCX',
    'xls': 'XLS',
    'xlsx': 'XLSX',
    'ppt': 'PPT',
    'pptx': 'PPTX',
    'txt': 'TXT',
    'zip': 'ZIP'
  };
  return typeMap[extension] || 'FILE';
};

// 下载资源数据
const downloadResources = ref({});
// FAQ数据
const faqs = ref([]);
// 技术文档数据
const documents = ref([]);  // 添加技术文档数据变量

// 从API获取下载资源
const fetchDownloadResources = async () => {
  try {
    // 获取当前语言
    const currentLang = locale.value === 'en' ? 'en' : 'zh';
    // 调用后端API获取资源数据，传递语言参数
    const response = await getResources({ params: { lang: currentLang } });
    // 按类型组织资源数据
    downloadResources.value = {
      software: response.data.filter(item => item.type === 'software'),
      manual: response.data.filter(item => item.type === 'manual')
    };
  } catch (error) {
    console.error('获取下载资源失败:', error);
  }
};

// 从API获取FAQ数据
const fetchFaqs = async () => {
  try {
    // 获取当前语言
    const currentLang = locale.value === 'en' ? 'en' : 'zh';
    const response = await getFaqs({ params: { lang: currentLang } });
    // 检查响应格式
    if (response.data && Array.isArray(response.data)) {
      faqs.value = response.data;
    } else if (response.data && response.data.error) {
      console.error('获取FAQ数据失败:', response.data.error);
      faqs.value = [];
    } else {
      faqs.value = [];
    }
  } catch (error) {
    faqs.value = [];
  }
};

// 从API获取技术文档数据
const fetchDocuments = async () => {
  try {
    // 获取当前语言
    const currentLang = locale.value === 'en' ? 'en' : 'zh';
    // 调用后端API获取技术文档数据，传递语言参数
    const response = await getDocuments({ params: { lang: currentLang } });
    documents.value = response.data || [];
  } catch (error) {
    console.error('获取技术文档失败:', error);
    documents.value = [];
  }
};

// 组件挂载时获取资源
onMounted(() => {
  fetchDownloadResources();
  fetchFaqs();
  fetchDocuments();  // 调用获取技术文档数据
});

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchDownloadResources();
  fetchFaqs();
  fetchDocuments();
});

// 优化下载处理函数
const handleDownload = async (item) => {
  try {
    // 从URL中提取文件名（如果item.filename不存在）
    let filename = item.filename;
    if (!filename && item.url) {
      const urlParts = item.url.split('/');
      filename = urlParts[urlParts.length - 1];
    }
    
    // 如果仍无法获取文件名，使用item.name作为备选
    if (!filename) {
      filename = item.name;
    }
    
    const response = await downloadFile(filename);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // 确保下载文件名包含扩展名
    let downloadName = filename; // 优先使用从URL提取的完整文件名
    if (!downloadName || !downloadName.includes('.')) {
      // 如果没有扩展名，尝试从item.url获取扩展名
      if (item.url && item.url.includes('.')) {
        const urlParts = item.url.split('/');
        const urlFilename = urlParts[urlParts.length - 1];
        if (urlFilename.includes('.')) {
          const extension = urlFilename.split('.').pop();
          downloadName = `${item.name || 'download'}.${extension}`;
        }
      }
    }
    
    link.setAttribute('download', downloadName);
    
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('下载文件失败:', error);
    alert(t('techSupport.downloadFailed'));
  }
};
</script>

<style scoped lang="scss">
@import '@/styles/sections/product_page/product_head';
@import '@/styles/sections/techsupportpage/TechSupportPage';

</style>